import { ArrowUpRight, ShieldCheck, Award, ShoppingBag, Loader2, ChevronRight, Heart, AlertTriangle, RotateCw } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { HOME, WISHLIST } from '@/constants/testIds';
import useWishlist from '@/hooks/useWishlist';
import CheckoutConsentModal from '@/components/CheckoutConsentModal';
import { Product360Modal } from '@/components/Product360Viewer';
import { DEFAULT_360_FRAMES } from '@/data/product360Frames';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ProductGrid({ id, eyebrow, title, lead, items, testId, columns = 3, eapcCompliant = false, brassBadge = false, activeAdaptiveTag = null }) {
  const colClass = columns === 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3';
  const [busySlug, setBusySlug] = useState(null);
  const [buyError, setBuyError] = useState(null);
  const [consentProduct, setConsentProduct] = useState(null);
  const { isSaved, toggle } = useWishlist();
  // Which product's 360° viewer is currently open (null = none).
  const [viewer360, setViewer360] = useState(null);

  const scrollToEnquiry = (slug, productName) => {
    const el = document.getElementById('contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    // Dispatch a CustomEvent so the Enquiry component can update React state.
    window.dispatchEvent(
      new CustomEvent('ecobike:prefill-enquiry', {
        detail: {
          subject: `Enquiry: ${productName || slug}`,
          interest: id === 'ebikes' ? 'e-bikes' : 'camping',
        },
      }),
    );
  };

  // Two-step Buy Now: opens consent modal first, only redirects to Stripe
  // once the customer ticks "I have read and agree" and clicks Continue.
  const openConsent = (product) => {
    setBuyError(null);
    setConsentProduct(product);
  };

  const confirmAndCheckout = async () => {
    if (!consentProduct) return;
    const slug = consentProduct.slug;
    setBusySlug(slug);
    try {
      const res = await axios.post(`${API}/checkout/session`, {
        slug,
        origin_url: window.location.origin,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      setBuyError(err?.response?.data?.detail || 'Could not start checkout. Please try again.');
      setBusySlug(null);
      setConsentProduct(null);
    }
  };

  return (
    <section id={id} data-testid={testId} className="section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <span className="font-eyebrow">{eyebrow}</span>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl text-[var(--brand-forest)] leading-[1.02] tracking-tight">
              {title}
            </h2>
          </div>
          <p className="text-[var(--brand-muted)] text-base lg:text-lg leading-relaxed max-w-md">
            {lead}
          </p>
        </div>

        {eapcCompliant && (
          <div
            data-testid="practical-terrain-note"
            className="mb-6 inline-flex items-start gap-3 bg-[var(--brand-forest)]/5 border border-[var(--brand-olive)]/30 px-5 py-4 max-w-3xl"
          >
            <ShieldCheck size={20} className="text-[var(--brand-olive)] mt-0.5 shrink-0" />
            <p className="text-sm leading-relaxed text-[var(--brand-ink)]">
              <span className="font-semibold text-[var(--brand-forest)]">
                Practical Terrain &amp; Utility Advice.
              </span>{' '}
              Every frame is chosen for the ground it&rsquo;s built to survive
              &mdash; mud, hills, gates, and getting on and off in
              waterproofs. Talk with our team about which bike suits
              your local terrain and your specific needs.{' '}
              <a href="/terms" className="link-expand font-semibold text-[var(--brand-forest)]">
                Read the small print
              </a>
              .
            </p>
          </div>
        )}

        {brassBadge && (
          <div
            data-testid="brass-badge-note"
            className="mb-10 flex items-start gap-3 bg-[var(--brand-forest)] text-[var(--brand-parchment)] px-5 py-5 max-w-3xl relative overflow-hidden"
          >
            <div className="grain absolute inset-0 opacity-40 pointer-events-none" />
            <Award size={22} className="text-[var(--brand-gold)] mt-0.5 shrink-0 relative" strokeWidth={1.5} />
            <p className="text-sm leading-relaxed relative">
              <span className="font-display text-base text-[var(--brand-gold)] block mb-1">
                Every bike carries its mark.
              </span>
              Tucked inside the user-manual sleeve of every EcoBike you&rsquo;ll
              find a <strong>numbered Certificate of Authenticity</strong>
              &mdash; printed on heavy kraft card, embossed with the Ben Lomond
              mark, hand-numbered, sealed with our gamekeeper&rsquo;s gold wax
              stamp and signed in Perthshire. Keep it with your logbook; it
              records the build number, frame ID and the date the bike left
              for you. Small thing. Quietly satisfying.
            </p>
          </div>
        )}

        {/* Swipe hint — mobile only. Tells one-handed users they can swipe. */}
        <div
          data-testid={`${id}-swipe-hint`}
          className="sm:hidden mb-4 flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase text-[var(--brand-muted)]"
          aria-hidden="true"
        >
          <span>Swipe to browse</span>
          <ChevronRight size={14} className="swipe-nudge text-[var(--brand-olive)]" />
        </div>

        <div
          role="region"
          aria-label={`${eyebrow} carousel`}
          aria-roledescription="carousel"
          data-testid={`${id}-carousel`}
          className={`flex sm:grid sm:grid-cols-2 ${colClass} gap-4 sm:gap-6 overflow-x-auto sm:overflow-x-visible snap-x snap-mandatory sm:snap-none scrollbar-hide -mx-6 sm:mx-0 px-6 sm:px-0 pb-4 sm:pb-0 overscroll-x-contain`}
        >
          {items.map((p) => {
            const matchesFilter =
              activeAdaptiveTag &&
              Array.isArray(p.adaptiveTags) &&
              p.adaptiveTags.includes(activeAdaptiveTag);
            const saved = isSaved(p.slug);
            return (
            <article
              key={p.slug}
              data-testid={HOME.productCard(p.slug)}
              className={`product-card flex flex-col shrink-0 sm:shrink w-[85vw] max-w-[22rem] sm:w-auto sm:max-w-none snap-center sm:snap-align-none ${
                matchesFilter ? 'brass-glow' : ''
              }`}
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-[var(--brand-parchment)]">
                <img
                  src={p.image}
                  alt={p.name}
                  className="absolute inset-0 w-full h-full object-contain editorial-img transition-transform duration-700 hover:scale-105"
                />
                {p.badge && (
                  <span className="absolute top-3 left-3 bg-[var(--brand-forest)] text-[var(--brand-parchment)] text-[10px] tracking-[0.22em] uppercase px-2.5 py-1">
                    {p.badge}
                  </span>
                )}
                {/* Wishlist heart button — top-right of image */}
                <button
                  type="button"
                  data-testid={WISHLIST.heart(p.slug)}
                  aria-label={saved ? `Remove ${p.name} from saved items` : `Save ${p.name}`}
                  aria-pressed={saved}
                  onClick={() => toggle({ slug: p.slug, name: p.name, price: p.price, image: p.image })}
                  className={
                    'absolute top-3 right-3 w-9 h-9 grid place-items-center bg-[var(--brand-parchment)]/90 border border-[var(--brand-stone)] hover:border-[var(--brand-gold)] transition-all ' +
                    (saved ? 'text-[var(--brand-gold)]' : 'text-[var(--brand-forest)]')
                  }
                >
                  <Heart size={16} className={saved ? 'fill-[var(--brand-gold)]' : ''} />
                </button>
                {/* 360° view launcher — bottom-right of image. Only
                    rendered on e-bike cards that have their own frame set;
                    an enquire-only card without frames doesn't show it. */}
                {eapcCompliant && p.frames360 && (
                  <button
                    type="button"
                    onClick={() => setViewer360(p)}
                    data-testid={`product-360-open-${p.slug}`}
                    aria-label={`View ${p.name} in 360-degree rotation viewer`}
                    className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 bg-[var(--brand-forest-deep)] hover:bg-[var(--brand-forest)] text-[var(--brand-gold)] px-3 py-1.5 text-[10px] tracking-[0.22em] uppercase font-semibold transition-colors group"
                  >
                    <RotateCw size={12} strokeWidth={2.5} className="group-hover:rotate-90 transition-transform duration-500" />
                    360° View
                  </button>
                )}
              </div>
              <div className="p-6 flex flex-col grow">
                <div className="flex items-baseline justify-between gap-3">
                  <h3 className="font-display text-2xl text-[var(--brand-forest)] tracking-tight">
                    {p.name}
                  </h3>
                  <span className="text-sm font-semibold text-[var(--brand-olive)] whitespace-nowrap">
                    {p.price}
                  </span>
                </div>
                <p className="mt-1 text-sm text-[var(--brand-muted)] italic">{p.subtitle}</p>
                <p className="mt-3 text-sm text-[var(--brand-ink)] leading-relaxed">{p.blurb}</p>

                <ul className="mt-4 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs text-[var(--brand-muted)]">
                  {p.spec.map((s) => (
                    <li key={s} className="flex items-center gap-2">
                      <span className="w-1 h-1 bg-[var(--brand-gold)]" />
                      {s}
                    </li>
                  ))}
                </ul>

                {/* Trust pill — links to the Compliance page. Only shown on
                    e-bike cards (when eapcCompliant is true). Reframed post-pivot
                    to emphasise our team's practical terrain advice rather
                    than the underlying legal compliance. */}
                {eapcCompliant && (
                  <Link
                    to="/compliance"
                    data-testid={`trust-badge-${p.slug}`}
                    className="mt-5 self-start inline-flex items-center gap-2 border border-[var(--brand-gold-soft)] bg-[var(--brand-gold)]/10 hover:bg-[var(--brand-gold)]/20 hover:border-[var(--brand-gold)] transition-colors px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--brand-forest)] group"
                    aria-label="Read our practical terrain advice and the small print"
                  >
                    <ShieldCheck size={12} strokeWidth={2.2} className="text-[var(--brand-forest)]" />
                    Practical Terrain &middot; Read the Small Print
                    <ArrowUpRight size={11} className="text-[var(--brand-forest)] opacity-60 group-hover:opacity-100 transition-opacity" />
                  </Link>
                )}

                {/* CTA row. Full-width Enquire button on enquire-only
                    cards (e.g. bikes still awaiting UK EAPC confirmation);
                    two-column Enquire + Buy Now otherwise. Both variants
                    keep 44px minimum touch targets for one-handed use. */}
                <div className={`mt-6 pt-5 border-t border-[var(--brand-stone)] grid ${p.enquireOnly ? 'grid-cols-1' : 'grid-cols-2'} gap-3`}>
                  <button
                    data-testid={HOME.productEnquireBtn(p.slug)}
                    onClick={() => scrollToEnquiry(p.slug, p.name)}
                    className="min-h-[44px] border border-[var(--brand-forest)] text-[var(--brand-forest)] text-sm font-semibold inline-flex items-center justify-center gap-1.5 hover:bg-[var(--brand-forest)] hover:text-[var(--brand-parchment)] transition-colors"
                  >
                    {p.enquireOnly ? 'Enquire about this bike' : 'Enquire'}
                    <ArrowUpRight size={14} />
                  </button>
                  {!p.enquireOnly && (
                    <button
                      data-testid={HOME.productBuyBtn(p.slug)}
                      onClick={() => openConsent(p)}
                      disabled={busySlug === p.slug}
                      className="btn-gold min-h-[44px] px-4 text-sm font-semibold tracking-wide inline-flex items-center justify-center gap-1.5 disabled:opacity-60 disabled:cursor-wait"
                    >
                      {busySlug === p.slug ? (
                        <>
                          <Loader2 size={13} className="animate-spin" />
                          Starting…
                        </>
                      ) : (
                        <>
                          <ShoppingBag size={13} />
                          Buy now
                        </>
                      )}
                    </button>
                  )}
                </div>

                {/* Per-product disclaimer banner (EAPC compliance for the
                    Juliet 2, 18+ for the Field Knife). Sits below the CTA
                    row so the buyer sees it before they click Buy Now. */}
                {p.disclaimer && (
                  <div
                    data-testid={`product-disclaimer-${p.slug}`}
                    className="mt-4 flex items-start gap-2 bg-[var(--brand-parchment-warm)] border border-[var(--brand-olive)]/25 border-l-4 border-l-[var(--brand-gold)] px-3 py-2.5"
                  >
                    <AlertTriangle size={13} className="shrink-0 mt-0.5 text-[var(--brand-forest-deep)]" strokeWidth={2.25} />
                    <div className="min-w-0">
                      <div className="text-[9px] uppercase tracking-[0.2em] font-bold text-[var(--brand-forest-deep)]">
                        {p.disclaimer.label}
                      </div>
                      <p className="mt-0.5 text-[11.5px] leading-relaxed text-[var(--brand-ink)]">
                        {p.disclaimer.text}{' '}
                        <Link to="/disclaimer" className="link-expand font-semibold text-[var(--brand-forest-deep)]">
                          Full disclaimer &rarr;
                        </Link>
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </article>
            );
          })}
        </div>

        {buyError && (
          <p
            data-testid={`${id}-buy-error`}
            className="mt-6 text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-3 max-w-xl"
          >
            {buyError}
          </p>
        )}
      </div>

      <CheckoutConsentModal
        product={consentProduct}
        busy={!!busySlug}
        onCancel={() => setConsentProduct(null)}
        onConfirm={confirmAndCheckout}
      />

      <Product360Modal
        open={!!viewer360}
        onClose={() => setViewer360(null)}
        frames={viewer360?.frames360 || DEFAULT_360_FRAMES}
        productName={viewer360?.name || ''}
      />
    </section>
  );
}
